import { CircleCheck, CircleX } from "lucide-react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";

import {
  CustomInput,
  CustomLabel,
  FieldErrorMessage,
} from "@/components/atoms";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";

interface InputControlProps<T extends FieldValues>
  extends React.InputHTMLAttributes<HTMLInputElement> {
  formControl: Control<T>;
  nameField: FieldPath<T>;
  label?: string | React.ReactNode;
  placeholder?: string;
  typeField?: React.HTMLInputTypeAttribute;
  classedName?: string;
  extraButton?: {
    text: string;
    onClick: () => void;
    testId?: string;
    disabled?: boolean;
  };
  infoMessage?: string | React.ReactNode;
  isError?: boolean;
  isPercent?: boolean;
  errorClasses?: string;
}

const InputControl = <T extends FieldValues>({
  formControl,
  nameField,
  label,
  placeholder,
  typeField = "text",
  classedName = "mb-5.5",
  extraButton,
  infoMessage,
  isError = false,
  isPercent = false,
  errorClasses,
  ...rest
}: InputControlProps<T>) => {
  const { setValue } = useFormContext<T>();

  return (
    <FormField
      control={formControl}
      name={nameField}
      render={({ field }) => (
        <FormItem className={cn("relative h-fit w-full gap-1", classedName)}>
          <div className={cn("flex items-center justify-between gap-1")}>
            {label && <CustomLabel htmlFor={nameField} label={label} />}
            {infoMessage && (
              <div className="flex items-center gap-1">
                {isError ? (
                  <CircleX className="w-4 h-4 text-red-500" />
                ) : (
                  <CircleCheck className="w-4 h-4 text-[#65A30D]" />
                )}
                <span className="text-[#65A30D] text-xs">{infoMessage}</span>
              </div>
            )}
          </div>
          <FormControl>
            <CustomInput
              id={nameField}
              {...field}
              type={typeField}
              placeholder={placeholder}
              extraButton={extraButton}
              {...rest}
              onPaste={e => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text");
                const trimmed = pasted.trim();
                setValue(nameField, trimmed as T[typeof nameField], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              isPercent={isPercent}
            />
          </FormControl>
          <FieldErrorMessage className={errorClasses} />
        </FormItem>
      )}
    />
  );
};

export default InputControl;
